import InfoTooltip from '@/components/Controls/Tooltips/InfoTooltip';
import DroppablePromptList from '@/components/GamePacks/Prompts/DroppablePromptList';
import { PromptExtended } from '@/components/GamePacks/Prompts/PromptList';
import ClockIcon from '@/components/Icons/ClockIcon';
import InfoIcon from '@/components/Icons/InfoIcon';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import React, { useMemo, useState } from 'react';
import sandClockImage from '@/assets/images/sand-clock.png';

interface GameSidebarProps {
  launchGame: () => void;
  clear: () => void;
  onDragEnd: (result: DropResult) => void;
  selectedPrompts: PromptExtended[];
  setSelectedPrompts: React.Dispatch<React.SetStateAction<PromptExtended[]>>;
  loading: boolean;
  keepTip: boolean;
}

export default function GameSidebar({
  clear,
  onDragEnd,
  selectedPrompts,
  setSelectedPrompts,
  launchGame,
  loading,
  keepTip
}: GameSidebarProps) {
  const [playersCount, setPlayersCount] = useState(0);

  const estimatedTime = useMemo(() => {
    return isNaN(playersCount) ? 0 : selectedPrompts.length * playersCount;
  }, [playersCount, selectedPrompts]);

  const handleClear = () => {
    setPlayersCount(NaN);
    clear();
  };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex flex-col rounded-xs px-24 py-32 bg-white w-330 shrink-0">
          <div className="flex justify-between pb-12 border-b border-neutral-200">
            <h3 className="text-h3">Your Game</h3>
            <button
              className="link link-sm"
              onClick={handleClear}
              disabled={selectedPrompts.length === 0}
            >
              Clear All
            </button>
          </div>
          <div className="py-12 border-b border-neutral-200">
            <div className="flex gap-4 items-center mb-12">
              <p className="text-body text-body-strong">Selected Prompts</p>
              <p className="text-body-sm">(max 2)</p>
              <InfoTooltip render={() => <InfoIcon className="w-15 h-15 text-primary-500" />}>
                <span className="text-body-xs">
                  Each game allows 2 prompts max. To swap, remove a selected prompt first.
                </span>
              </InfoTooltip>
            </div>
            {selectedPrompts.length === 0 ? (
              <div className="rounded-xs bg-primary-100 py-40">
                <p className="text-body-sm text-neutral-400 text-center">
                  No prompts selected yet.
                </p>
              </div>
            ) : (
              <DroppablePromptList
                emptyMessage="No prompts selected"
                prompts={selectedPrompts}
                droppableId="selectedPrompts"
                setSelectedPrompts={setSelectedPrompts}
              />
            )}
          </div>
          <div className="pt-12">
            {(selectedPrompts.length === 1 || (keepTip && selectedPrompts.length > 1)) && (
              <div className="p-12 rounded-xs bg-neutral-100 flex justify-start items-start gap-12">
                <img src={sandClockImage} className="w-20" />
                <div>
                  <p className="text-body text-body-sm-strong">Not sure how long Scribl takes?</p>
                  <p className="text-body text-body-sm text-neutral-400">
                    Add your player count below for a rough estimate (min. 2 players)
                  </p>
                </div>
              </div>
            )}
            <div className="flex justify-start items-center gap-8 mb-12 mt-12">
              <input
                type="integer"
                name="players-qty"
                placeholder="Players Qty"
                value={isNaN(playersCount) || playersCount === 0 ? '' : playersCount}
                onChange={(e) => {
                  e.preventDefault();
                  const count = parseInt(e.target.value);
                  if (count > 999) return;
                  setPlayersCount(parseInt(e.target.value));
                }}
                className="rounded-3xs text-[14px] leading-24 border-1 border-neutral-200 focus:border-neutral-500 px-12 py-4 w-95 text-neutral-500 placeholder:text-neutral-200 outline-none"
              />
              <p className="text-body text-body-sm-strong">x</p>
              <p className="text-body text-body-sm-strong">{selectedPrompts.length}</p>
              <p className="text-body text-body-sm-strong">
                {selectedPrompts.length === 1 ? 'prompt' : 'prompts'}
              </p>
            </div>
            <div className="flex justify-start items-center gap-4 mb-24">
              <ClockIcon className="w-16 h-16" />
              <p className="text-body text-body-sm">Estimated Game Time:</p>
              <p className="text-body text-body-sm-strong">{estimatedTime}</p>
              <p className="text-body text-body-sm-strong">
                {estimatedTime === 1 ? 'min' : 'mins'}
              </p>
            </div>
            <button
              className="btn btn-primary w-full"
              onClick={launchGame}
              disabled={selectedPrompts.length === 0 || loading}
            >
              Start Game
            </button>
          </div>
        </div>
      </DragDropContext>
    </>
  );
}
